import { PUI } from '@pleased-npm/fe-customisation';
import type { CustomisationStore } from '@/common/customisation/stores/customisation.store';
import type { GetPageFieldParams } from '@/common/customisation/composables/useGetPageField';
import {
  findBlockByKey,
  findFieldByKey,
  findFieldInBlock,
  findFieldInDocument,
  findSectionByKey,
  findSectionInDocument,
} from '@/common/customisation/utils/document.utils';
import { getFieldValue } from '@/common/customisation/utils/field.accessors';
import { getHydratedThemeForRegistry } from '@/common/customisation/utils/registryLoader';

/** Same shape as `useGetPageField`; theme reads are resolved from the active hydrated theme. */
export type GetThemeFieldParams = GetPageFieldParams;

/**
 * Params for the default user display name field on the theme (for use with `useGetPageField` / `getThemeField`).
 */
export const THEME_DEFAULT_USER_NAME_FIELD_PARAMS = {
  registryId: PUI.REGISTRY_IDS.DEFAULT_THEME,
  blockKey: PUI.THEME_BLOCK_KEYS.DEFAULT_PARAMETERS,
  sectionKey: PUI.THEME_SECTION_KEYS.USER_NAME,
  fieldKey: PUI.THEME_FIELD_KEYS.DEFAULT_USER_NAME,
} satisfies GetThemeFieldParams;

/**
 * Read a field from the **active theme** (hydrated), using the same navigation rules as `useGetPageField`:
 * block+section → block → section → document.
 *
 * Pass the customisation store explicitly so this stays valid outside Vue `setup` and avoids hidden `useStore()` calls.
 * `registryId` must be `PUI.REGISTRY_IDS.DEFAULT_THEME` (theme-backed fields); otherwise returns `null`.
 */
export const getThemeField = <T = string>(
  store: CustomisationStore,
  { registryId, fieldKey, sectionKey, blockKey }: GetThemeFieldParams
): T | null => {
  if (registryId !== PUI.REGISTRY_IDS.DEFAULT_THEME) {
    return null;
  }

  const hydratedTheme = getHydratedThemeForRegistry(store.activeTheme);

  try {
    if (blockKey && sectionKey) {
      const block = findBlockByKey(hydratedTheme, blockKey);
      const section = findSectionByKey(block, sectionKey);
      const field = findFieldByKey(section, fieldKey);
      return getFieldValue<T>(field);
    }

    if (blockKey) {
      const block = findBlockByKey(hydratedTheme, blockKey);
      const field = findFieldInBlock(block, fieldKey);
      return getFieldValue<T>(field);
    }

    if (sectionKey) {
      const section = findSectionInDocument(hydratedTheme, sectionKey);
      const field = findFieldByKey(section, fieldKey);
      return getFieldValue<T>(field);
    }

    const field = findFieldInDocument(hydratedTheme, fieldKey);
    return getFieldValue<T>(field);
  } catch {
    return null;
  }
};
